// src/pages/Dashboard/teacher/paperEvaluation/PaperEvaluationQuestion.jsx
import React from "react";
import {
  Upload,
  CheckCircle,
  Clock,
  AlertCircle,
  X as CloseIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const PaperEvaluationQuestion = ({
  section,
  question,
  sectionIndex,
  questionIndex,
  studentId,
  uploadProgress,
  onFileSelect,
  onRemoveFile,
  onRemoveQuestion,
  onOpenVerifyModal,
  readyForGrading,
  evaluationResult,
}) => {
  const { t } = useTranslation();

  // ---------- ICON BASED ON STATUS ----------
  const getStatusIcon = (status) => {
    switch (status) {
      case "uploading":
        return <Clock className="text-yellow-500" size={16} />;
      case "uploaded":
        return <CheckCircle className="text-blue-500" size={16} />;
      case "ocr_complete":
        return <CheckCircle className="text-green-500" size={16} />;
      case "error":
        return <AlertCircle className="text-red-500" size={16} />;
      default:
        return null;
    }
  };

  const getImagePreview = (file) => URL.createObjectURL(file);

  return (
    <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50/40">

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
        <div>
          <h3 className="text-base font-semibold text-gray-800">
            {question.question_id}
          </h3>

          <p className="text-xs text-gray-500">
            {question.files.length}{" "}
            {question.files.length === 1
              ? t("paperEval.page")
              : t("paperEval.pages")}{" "}
            {t("paperEval.attached")}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={!readyForGrading}
            onClick={() => onOpenVerifyModal(sectionIndex, questionIndex)}
            className="px-3 py-1.5 rounded-md text-xs sm:text-sm font-semibold text-white 
                       bg-emerald-600 hover:bg-emerald-700
                       disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {t("paperEval.verifyGrade")}
          </button>

          {section.questions.length > 1 && (
            <button
              onClick={() => onRemoveQuestion(sectionIndex, questionIndex)}
              className="text-xs sm:text-sm text-red-600 hover:text-red-800"
            >
              {t("paperEval.remove")}
            </button>
          )}
        </div>
      </div>

      {/* UPLOAD AREA */}
      <div className="mb-3">
        <label className="flex items-center justify-center w-full px-4 py-6 border-2 
                           border-dashed border-gray-300 rounded-lg cursor-pointer 
                           hover:border-blue-500 hover:bg-blue-50 transition-all bg-white">
          <div className="text-center">
            <Upload className="mx-auto mb-2 text-gray-400" size={32} />

            <span className="text-sm font-medium text-gray-700">
              {t("paperEval.uploadCTA")}
            </span>

            <span className="block text-xs text-gray-500 mt-1">
              {t("paperEval.multiSelect")}
            </span>

            {question.files.length > 0 && (
              <span className="block text-sm text-blue-600 mt-2 font-medium">
                {question.files.length}{" "}
                {question.files.length === 1 ? t("paperEval.image") : t("paperEval.images")}{" "}
                {t("paperEval.selected")}
              </span>
            )}
          </div>

          <input
            type="file"
            multiple
            accept="image/*"
            onChange={(e) =>
              onFileSelect(sectionIndex, questionIndex, e.target.files)
            }
            className="hidden"
          />
        </label>
      </div>

      {/* IMAGE PREVIEWS */}
      {question.files.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              {t("paperEval.pages")} ({question.files.length})
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {question.files.map((file, fileIndex) => {
              const key = `${studentId}_${section.section_type}_${question.question_id}_${fileIndex}`;
              const status = uploadProgress[key];

              return (
                <div key={fileIndex}
                  className="relative group border border-gray-200 rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow"
                >
                  <div className="aspect-square bg-gray-100 flex items-center justify-center">
                    <img
                      src={getImagePreview(file)}
                      alt={`Preview ${fileIndex + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="p-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-700">
                        {t("paperEval.page")} {fileIndex + 1}
                      </span>
                      {status && getStatusIcon(status.status)}
                    </div>

                    <div className="text-xs text-gray-500 truncate">
                      {file.name}
                    </div>

                    {status?.status === "ocr_complete" && (
                      <div className="mt-1 text-xs text-green-600 font-medium">
                        ✓ {t("paperEval.ocrDone")}
                      </div>
                    )}

                    {status?.status === "uploading" && (
                      <div className="mt-1 text-xs text-yellow-600">
                        {t("paperEval.uploading")}
                      </div>
                    )}

                    {status?.status === "error" && (
                      <div className="mt-1 text-xs text-red-600">
                        {t("paperEval.uploadError")}
                      </div>
                    )}
                  </div>

                  {/* REMOVE BUTTON */}
                  <button
                    onClick={() =>
                      onRemoveFile(sectionIndex, questionIndex, fileIndex)
                    }
                    className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full 
                               opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    title={t("paperEval.removeImage")}
                  >
                    <CloseIcon size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* EVALUATION RESULT */}
      {evaluationResult && (
        <div className="mt-4 border border-emerald-100 bg-emerald-50 rounded-md p-3">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <CheckCircle size={18} className="text-emerald-600" />
              <span className="text-sm font-semibold text-emerald-800">
                {t("paperEval.evalComplete")}
              </span>
            </div>

            <span className="text-xs font-medium text-emerald-700">
              {evaluationResult.status}
            </span>
          </div>

          <p className="text-sm text-gray-700 mb-1">
            {t("paperEval.marks")} {evaluationResult.marks_obtained} / {evaluationResult.total_marks}
          </p>

          <p className="text-xs text-gray-600 whitespace-pre-line">
            {evaluationResult.detailed_feedback}
          </p>
        </div>
      )}
    </div>
  );
};

export default PaperEvaluationQuestion;
